package ru.it_mozaika.sender.js.application.theme

import js.core.jso
import mui.material.styles.Mixins
import mui.material.styles.MixinsOptions
import mui.material.styles.PaletteOptions
import mui.material.styles.TypographyOptions
import mui.material.styles.TypographyOptionsBuilder
import mui.material.styles.TypographyVariant
import mui.material.styles.createPalette
import mui.material.styles.createTheme
import mui.system.Direction
import react.CSSProperties
import web.cssom.AutoLengthProperty
import web.cssom.Color
import web.cssom.FontFamily
import web.cssom.FontWeight
import web.cssom.MarginTop
import web.cssom.TextTransform
import web.cssom.em
import web.cssom.important

external interface Mixins : MixinsOptions, Mixins

val lightTheme = createTheme(jso {
    direction = Direction.ltr
    palette = lightPalette
    mixins = jso<ru.it_mozaika.sender.js.application.theme.Mixins> {
        toolbar = jso {
            minHeight = "48px"
            padding = "16px"
            maxHeight = "70px"
            this["@media (min-width: 600px)"] = jso {
                minHeight = "48px"
            }
        }
    }
    typography = TypographyOptions {
        asDynamic().fontFamily = "Roboto, sans-serif"
        this[TypographyVariant.h6] = jso {
            asDynamic().fontWeight = 500
            asDynamic().fontSize = "0.75rem"
            color = Color(lightPalette.grey["900"] as String)
        }
        this[TypographyVariant.h5] = jso {
            asDynamic().fontWeight = 500
            asDynamic().fontSize = "0.875rem"
            color = Color(lightPalette.grey["900"] as String)
        }
        this[TypographyVariant.h4] = jso {
            asDynamic().fontWeight = 600
            asDynamic().fontSize = "1rem"
            color = Color(lightPalette.grey["900"] as String)
        }
        this[TypographyVariant.h3] = jso {
            asDynamic().fontWeight = 700
            asDynamic().fontSize = "1.25rem"
            color = Color(lightPalette.grey["900"] as String)
        }
        this[TypographyVariant.h2] = jso {
            asDynamic().fontWeight = 700
            asDynamic().fontSize = "1.5rem"
            color = Color(lightPalette.grey["900"] as String)
        }
        this[TypographyVariant.h1] = jso {
            asDynamic().fontWeight = 700
            asDynamic().fontSize = "2.125rem"
            color = Color(lightPalette.grey["900"] as String)
        }
        this[TypographyVariant.subtitle1] = jso {
            asDynamic().fontWeight = 500
            asDynamic().fontSize = "0.875rem"
            color = Color(lightPalette.grey["900"] as String)
        }
        this[TypographyVariant.subtitle2] = jso {
            asDynamic().fontWeight = 400
            asDynamic().fontSize = "0.75rem"
            color = Color(lightPalette.grey["500"] as String)
        }
        this[TypographyVariant.subtitle2] = jso {
            asDynamic().fontWeight = 400
            asDynamic().fontSize = "0.75rem"
            color = Color(lightPalette.grey["500"] as String)
        }
        this[TypographyVariant.caption] = jso {
            asDynamic().fontWeight = 400
            asDynamic().fontSize = "0.75rem"
            color = Color(lightPalette.grey["500"] as String)
        }
        this[TypographyVariant.body1] = jso {
            asDynamic().fontWeight = 400
            asDynamic().fontSize = "0.875rem"
            lineHeight = 1.334.em
        }
        this[TypographyVariant.body2] = jso {
            letterSpacing = 0.em
            asDynamic().fontWeight = 400
            lineHeight = 1.5.em
            color = Color(lightPalette.grey["700"] as String)
        }
        this[TypographyVariant.button] = jso {
            textTransform = TextTransform.capitalize
        }
        asDynamic().customInput = jso<CSSProperties> {
            asDynamic().marginTop = 1
            asDynamic().marginBottom = 1
            asDynamic()["& > label"] = jso {
                this.top = 23
                this.left = 0
                this.color = lightPalette.grey["500"]
                this["&[data-shrink=\"false\"]"] = jso {
                    this.top = 5
                }
            }
            asDynamic()["& > div > input"] = jso {
                this.padding = "30.5px 14px 11.5px !important"
            }
            asDynamic()["& legend"] = jso {
                this.display = "none"
            }
            asDynamic()["& fieldset"] = jso {
                this.top = 0
            }
        }
        asDynamic().mainContent = jso {
            this.backgroundColor = lightPalette.background?.default.orEmpty()
            this.width = "100%"
            this.minHeight = "calc(100vh - 88px)"
            this.flexGrow = 1
            this.padding = "20px"
            this.marginTop = "88px"
            this.marginRight = "20px"
            this.borderRadius = "8px"
        }
        asDynamic().menuCaption = jso {
            this.fontSize = "0.875rem"
            this.fontWeight = 500
            this.color = lightPalette.grey["900"]
            this.padding = "6px"
            this.textTransform =  "capitalize"
            this.marginTop = "10px"
        }
        asDynamic().subMenuCaption = jso {
            this.fontSize = "0.6875rem"
            this.fontWeight = 500
            this.color = lightPalette.grey["500"]
            this.textTransform =  "capitalize"
        }
        asDynamic().commonAvatar = jso {
            this.cursor = "pointer"
            this.borderRadius = "8px"
        }
        asDynamic().smallAvatar = jso {
            this. width = "22px"
            this.height = "22px"
            this.fontSize = "1rem"
        }
        asDynamic().mediumAvatar = jso {
            this.width = "34px"
            this.height = "34px"
            this.fontSizez = "1.2rem"
        }
        asDynamic().largeAvatar = jso {
            this.width = "44px"
            this.height = "44px"
            this.fontSize = "1.5rem"
        }
    }
    components = jso {
        MuiButton = jso {
            styleOverrides = jso {
                root = jso {
                    fontWeight = 500
                    borderRadius = "4px"
                }
            }
        }
        MuiPaper = jso {
            defaultProps = jso {
                elevation = 0
            }
            styleOverrides = jso {
                root = jso {
                    color = lightPalette.grey["700"]
                    backgroundImage = "none"
                }
                rounded = jso {
                    borderRadius = "8px"
                }
            }
        }
        MuiCardHeader = jso {
            styleOverrides = jso {
                root = jso {
                    color = lightPalette.grey["900"]
                    padding = "24px"
                }
                title = jso {
                    fontSize = "1.125rem"
                }
            }
        }
        MuiCardContent = jso {
            styleOverrides = jso {
                root = jso {
                    padding = "24px"
                }
            }
        }
        MuiCardActions = jso {
            styleOverrides = jso {
                root = jso {
                    padding = "24px"
                }
            }
        }
        MuiListItemButton = jso {
            styleOverrides = jso {
                root = jso {
                    color = lightPalette.grey["700"]
                    paddingTop = "10px"
                    paddingBottom = "10px"
                    this["&.Mui-selected"] = jso {
                        color = lightPalette.secondary.dark
                        backgroundColor = lightPalette.secondary.light
                        this["&:hover"] = jso {
                            backgroundColor = lightPalette.secondary.light
                        }
                        this["& .MuiListItemIcon-root"] = jso {
                            color = lightPalette.secondary.dark
                        }
                    }
                    this["&:hover"] = jso {
                        backgroundColor = lightPalette.secondary.light
                        color = lightPalette.secondary.dark
                        this["& .MuiListItemIcon-root"] = jso {
                            color = lightPalette.secondary.dark
                        }
                    }
                }
            }
        }
        MuiListItemIcon = jso {
            styleOverrides = jso {
                root = jso {
                    color = lightPalette.grey["700"]
                    minWidth = "36px"
                }
            }
        }
        MuiListItemText = jso {
            styleOverrides = jso {
                primary = jso {
                    color = lightPalette.grey["900"]
                }
            }
        }
        MuiInputBase = jso {
            styleOverrides = jso {
                input = jso {
                    color = lightPalette.grey["900"]
                    this["&::placeholder"] = jso {
                        color = lightPalette.grey["500"]
                        fontSize = "0.875rem"
                    }
                }
            }
        }
        MuiOutlinedInput = jso {
            styleOverrides = jso {
                root = jso {
                    background = lightPalette.grey["50"]
                    borderRadius = "8px"
                    this["& .MuiOutlinedInput-notchedOutline"] = jso {
                        borderColor = lightPalette.grey["400"]
                    }
                    this["&:hover $notchedOutline"] = jso {
                        borderColor = lightPalette.primary.light
                    }
                    this["&.MuiInputBase-multiline"] = jso {
                        padding = 1
                    }
                }
                input = jso {
                    fontWeight = 500
                    background = lightPalette.grey["50"]
                    padding = "15.5px 14px"
                    borderRadius = "8px"
                    this["&.MuiInputBase-inputSizeSmall"] = jso {
                        padding = "10px 14px"
                        this["&.MuiInputBase-inputAdornedStart"] = jso {
                            paddingLeft = 0
                        }
                    }
                }
                inputAdornedStart = jso {
                    paddingLeft = 4
                }
                notchedOutline = jso {
                    borderRadius = "8px"
                }
            }
        }
        MuiSlider = jso {
            styleOverrides = jso {
                root = jso {
                    this["&.Mui-disabled"] = jso {
                        color = lightPalette.grey["300"]
                    }
                }
                mark = jso {
                    backgroundColor = lightPalette.background?.paper.orEmpty()
                    width = "4px"
                }
                valueLabel = jso {
                    color = lightPalette.primary.light
                }
            }
        }
        MuiDivider = jso {
            styleOverrides = jso {
                root = jso {
                    borderColor = lightPalette.grey["200"]
                    opacity = 1
                }
            }
        }
        MuiAvatar = jso {
            styleOverrides = jso {
                root = jso {
                    borderColor = lightPalette.primary.dark
                    background = lightPalette.primary["200"]
                }
            }
        }
        MuiTooltip = jso {
            styleOverrides = jso {
                tooltip = jso {
                    color = lightPalette.background?.paper.orEmpty()
                    background = lightPalette.grey["700"]
                }
            }
        }
        MuiTable = jso {
            styleOverrides = jso {
                root = jso {
//                    color = lightPalette.grey["900"]
                    this["& .hover"] = jso {
                        backgroundColor = lightPalette.secondary.light
                        color = lightPalette.secondary.dark
                    }
                }
            }
        }
        MuiTableCell = jso {
            styleOverrides = jso {
                root = jso {
                    color = lightPalette.grey["900"]
                }
            }
        }
        MuiTablePagination = jso {
            styleOverrides = jso {
                toolbar = jso {
                    alignItems = "baseline"
                }
            }
            defaultProps = jso {
                component = "div"
                rowsPerPageOptions = arrayOf<dynamic>(
                    jso{
                        value = 10
                        label = "10"
                    },
                    jso{
                        value = 25
                       label = "25"
                       },
                    jso{
                        value = 250
                    label = "250"
                       },
                    jso{
                    value = -1
                        label ="Выбрать все" }
                )
                labelRowsPerPage = "Показывать по..."
            }
        }
        MuiMenuItem = jso {
            styleOverrides = jso {
                root = jso {
                    color = lightPalette.grey["900"]
                }
            }
        }
        MuiStepButton = jso {
            styleOverrides = jso {
                root = jso {
                    color = lightPalette.grey["900"]
                }
                this["&.Mui-completed"] = jso {
                    color = lightPalette.grey["900"]
//                    fontWeight = 500
                }
            }
        }
        MuiStepLabel = jso {
            styleOverrides = jso {
                label = jso {
                    color = important(lightPalette.grey["900"])
//                    fontWeight = 500
                }
            }
        }
        MuiChip = jso {
            styleOverrides = jso {
                root = jso {
                    backgroundColor = lightPalette.secondary["50"]
                    color = lightPalette.grey["900"]
                    borderRadius = "16px"
                }
                colorError = jso {
                    backgroundColor = lightPalette.error["light"]
                    color = lightPalette.error.dark
                }
                colorSuccess = jso {
                    backgroundColor = lightPalette.success["light"]
                    color = lightPalette.success.dark
                }
                label = jso {
                    overflow = "hidden"
                    textOverflow = "ellipsis"
                    maxWidth = "200px"
                }
                deleteIcon = jso {
                    color = lightPalette.grey["900"]
                }
            }
        }
        MuiDialog = jso {
            styleOverrides = jso {
                paper = jso {
                    paddingTop = "12px"
                    paddingBottom = "12px"
                }
            }
        }
        MuiDialogTitle = jso {
            styleOverrides = jso {
                root = jso {
                    color = lightPalette.grey["900"]
                    fontSize = "1.25rem"
                }
            }
        }
        MuiDialogContentText = jso {
            styleOverrides = jso {
                root = jso {
                    color = lightPalette.grey["700"]
                    fontSize = "0.875rem"
                }
            }
        }
        MuiDialogActions = jso {
            styleOverrides = jso {
                root = jso {
                    paddingRight = "20px"
                }
            }
        }
        MuiFormControlLabel = jso {
            styleOverrides = jso {
                root = jso {
                    color = lightPalette.grey["700"]
                }
            }
        }
        MuiPickersDay = jso {
            styleOverrides = jso {
                root = jso {
                    color = lightPalette.grey["700"]
                }
            }
        }
    }
})
